<template>
  <section>
    <h2 class="section-title">Genomför ränteändring</h2>

    <AppValidationGroup v-slot="{ validate }">
      <div class="rate-form-grid">
        <AppSelect v-model="interestTerm" :options="interestTermOptions" label="Bindningstid" :rules="[required()]" />

        <AppTextField
          v-model="rate"
          type="amountWithDecimals"
          input-mode="decimal"
          label="Ränta"
          placeholder="t.ex. 3.45"
          suffix="%"
          :rules="[required(), minValue(MIN_RATE), maxValue(MAX_RATE)]"
        />

        <AppMenu>
          <template #activator="{ open }">
            <AppTextField
              v-model="validFrom"
              type="date"
              input-mode="numeric"
              placeholder="ÅÅÅÅ-MM-DD"
              label="Gäller fr.o.m."
              :rules="[required(), date(), dateIsWithinRange(earliestValidFromExclusive, latestValidFromExclusive)]"
              @focus="open"
            >
              <template #icon>
                <SvgIcon :path="mdiCalendar" />
              </template>
            </AppTextField>
          </template>

          <template #default="{ close }">
            <AppDatePicker v-model="validFrom" @input="close" />
          </template>
        </AppMenu>
      </div>

      <div v-if="allFieldsFilled" class="mt-3 text-center">
        <div class="confirmation-box">
          <p>OBS! Du genomför nu en ränteändring!</p>
          <p class="mt-3">
            Räntan ändras till <b>{{ formatPercentage(rate) }}</b>
            för lån med bindningstid
            <b>{{ translateInterestTerm(interestTerm) }}</b>
            från och med den <b>{{ validFrom }}.</b>
          </p>
          <p class="mt-3">Kontrollera uppgifterna innan du bekräftar.</p>
          <div class="mt-3 flex justify-center">
            <AppButton color="blue" :loading="isCreatingRate" @click="validate(() => void submitRate())">
              Bekräfta
            </AppButton>
          </div>
        </div>
      </div>
    </AppValidationGroup>
  </section>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { ref, computed } from 'vue'
import { mdiCalendar } from '@mdi/js'
import {
  AppSelect,
  AppButton,
  AppTextField,
  AppDatePicker,
  AppMenu,
  AppValidationGroup,
  SvgIcon,
  notify,
} from '@staff/ui'
import { formatPercentage, required, minValue, maxValue, date, dateIsWithinRange } from '@staff/shared'

import { usePricing } from '@/composables/usePricing'
import { useMetadata } from '@/composables/useMetadata'
import { RateRequestError } from '@/services/rates'
import { isInterestTerm } from '@/types/interest.types'

const MIN_RATE = 0.01
const MAX_RATE = 10
const MAX_MONTHS_AHEAD = 3

const { createRate, isCreatingRate, fetchRates, fetchAuditTrail } = usePricing()
const { interestTermOptions, translateInterestTerm } = useMetadata()

const interestTerm = ref('')
const rate = ref('')
const validFrom = ref('')

// dateIsWithinRange är exklusiv i båda ändar, så gränserna sätts ett dygn utanför det
// tillåtna intervallet. Tidigast tillåtna datum är i övermorgon — en ändring får aldrig gälla i morgon.
const earliestValidFromExclusive = computed(() => dayjs().add(1, 'day').format('YYYY-MM-DD'))
const latestValidFromExclusive = computed(() =>
  dayjs().add(MAX_MONTHS_AHEAD, 'month').add(1, 'day').format('YYYY-MM-DD'),
)

const allFieldsFilled = computed(() => !!interestTerm.value && !!rate.value && !!validFrom.value)

const createRateErrorMessage = (error: unknown) => {
  if (error instanceof RateRequestError) {
    if (error.status === 409) {
      return 'Det finns redan en ränta för det datumet och den bindningstiden.'
    }
    if (error.status === 403) {
      return 'Du saknar behörighet att ändra listräntor.'
    }
    if (error.status === 400) {
      return `Räntan kunde inte sparas: ${error.message}`
    }
  }

  return error instanceof Error ? error.message : 'Misslyckades med att spara ränta'
}

const submitRate = async () => {
  if (!isInterestTerm(interestTerm.value)) return

  try {
    await createRate({
      interestTerm: interestTerm.value,
      validFrom: validFrom.value,
      rate: Number(rate.value),
    })
    notify({ type: 'success', title: 'Räntan sparades' })

    interestTerm.value = ''
    rate.value = ''
    validFrom.value = ''

    await Promise.all([fetchRates(), fetchAuditTrail()])
  } catch (error: unknown) {
    notify({ type: 'error', title: createRateErrorMessage(error), dismissable: true })
  }
}
</script>

<style scoped lang="scss">
.section-title {
  margin-bottom: 16px;
}

.rate-form-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  container-type: inline-size;
}

@container (max-width: 48rem) {
  .rate-form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@container (max-width: 24rem) {
  .rate-form-grid {
    grid-template-columns: 1fr;
  }
}

.confirmation-box {
  display: inline-block;
  padding: 16px;
  border: 1px solid var(--color-border-default);
  border-radius: 4px;
  background-color: var(--color-canvas-subtle);
  text-align: left;
}
</style>
